import React, { memo, useMemo } from "react";
import Text from "../../../../shared-components/css-modules/lexus/atoms/Text";
import { type CarConfigDispatchType, useCarConfigSelector } from "../../../redux/store";
import styles from "./SearchResults.module.scss";
import {
    selectCarConfigSpecsSearchActiveFilters,
    selectCarConfigSpecsSearchValue,
    setActiveFilters,
    setActiveResult,
} from "../../../redux/slices/CarConfigSpecsSearchSlice";
import type { SpecDataType } from "../../../../../../common-deprecated/types/SpecTypes";
import Button from "../../../../shared-components/css-modules/lexus/atoms/Button";
import type { SpecGroupType } from "../../../hooks/useSpecs";
import { CarConfigLabel } from "../../../utils/labelUtils";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

const SearchResults: React.FC<{
    foundSpecs: SpecGroupType[];
    onResultClick?: (group: SpecGroupType) => void;
}> = ({ foundSpecs, onResultClick }) => {
    const dispatch = useDispatch<CarConfigDispatchType>();
    const searchValue = useCarConfigSelector(selectCarConfigSpecsSearchValue);
    const activeFilters = useCarConfigSelector(selectCarConfigSpecsSearchActiveFilters);

    // Specs are found specs but filtered based on active filters
    const specs = useMemo(() => {
        return foundSpecs.filter((item) => activeFilters.length === 0 || activeFilters.includes(item.id));
    }, [foundSpecs, activeFilters]);

    if (specs.length === 0) {
        return (
            <div className={styles.empty}>
                <Text tag="div" variant="apheleia-h4" spacing="spacing-1">
                    <CarConfigLabel useHTML label="carConfigSpecsResultsEmpty" params={{ searchValue }} />
                </Text>
                {specs.length !== foundSpecs.length && activeFilters.length !== 0 && (
                    <Text tag="div" variant="apheleia-body">
                        <CarConfigLabel label="carConfigSpecsResultsEmptyFilters" />
                        <Button
                            variant="clean"
                            onClick={() => {
                                dispatch(setActiveFilters(foundSpecs.map(({ id }) => id)));
                            }}
                        >
                            <CarConfigLabel
                                useHTML
                                label="carConfigSpecsResultsEmptyFiltersButton"
                                params={{ otherFilters: foundSpecs.map((item) => item.name).join(", ") }}
                            />
                        </Button>
                    </Text>
                )}
            </div>
        );
    }

    return (
        <ul className={`${styles.results} ${specs.length !== 0 ? styles.opened : styles.closed}`}>
            {specs.map((group, indexGroup) =>
                group.categories.map((category, indexCategory) =>
                    category.items.map((item, indexItem) => (
                        <li
                            key={`${group.id}-${category.id}-${item.id}`}
                            className={styles.result}
                            style={{
                                "--or-transition-delay": `${indexGroup + indexCategory + indexItem * 50}`,
                            }}
                        >
                            <button
                                onClick={() => {
                                    onResultClick?.(group);
                                    dispatch(
                                        setActiveResult({
                                            groupId: group.id,
                                            categoryId: category.id,
                                            itemId: item.id,
                                        }),
                                    );
                                }}
                                type="button"
                                className={styles.button}
                            >
                                <img src={group.image} alt="" role="presentation" />
                                <span>
                                    <Text tag="span" variant="apheleia-body-small">
                                        {group.name}
                                    </Text>
                                    <Text tag="p" variant="apheleia-h5">
                                        {item.name}
                                    </Text>
                                    <Text tag="span" variant="apheleia-body">
                                        {group.type === "specs" && (item as SpecDataType).value}
                                    </Text>
                                </span>
                            </button>
                        </li>
                    )),
                ),
            )}
        </ul>
    );
};

export default memo(SearchResults);
